import { Download as DownloadIcon } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PushPinIcon from "@mui/icons-material/PushPin";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { getGroupedRowModel } from "@tanstack/react-table";
import { MaterialReactTable } from "material-react-table";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  canonicalMonthColumnFromCsvHeader,
  isForecastCsvPeriodColumn,
  isForecastMonthColumn,
} from "../utils/forecastPeriodColumns";
import { formatSnapshotTime } from "../utils/snapshotTime";

// ---------- Constants ----------
const isMonthCol = isForecastMonthColumn;

/** Only these columns (and month columns) are editable in the table and allowed in CSV. */
const EDITABLE_FIELDS = new Set([
  "Curve",
  "Start Date",
  "End Date",
  "Forecast To Complete",
  "Notes",
]);
export function isEditableColumn(field) {
  return EDITABLE_FIELDS.has(field) || isMonthCol(field);
}

/** Show column if it's a month column, or (no underscore and starts with uppercase). */
function shouldDisplayColumn(key) {
  if (isMonthCol(key)) return true;
  if (key.includes("_")) return false;
  return /^[A-Z]/.test(key);
}

/** Keys that can be used for grouping: category and cost_code_level_1, cost_code_level_2, ... */
function isGroupingKey(key) {
  return key === "category" || /^cost_code_level_\d+$/.test(key);
}

/** Display label for grouping columns: category → "Cost Type", cost_code_level_1 → "Cost Code Tier 1", etc. */
function getGroupingColumnLabel(key) {
  if (key === "category") return "Cost Type";
  const match = key.match(/^cost_code_level_(\d+)$/);
  return match ? `Cost Code Tier ${match[1]}` : key;
}

/** Fixed order: these are not sorted alphabetically. Forecast To Complete is next to Notes. */
const FIXED_ORDER_BEFORE_LINE = [
  "Start Date",
  "End Date",
  "Curve",
  "Forecast To Complete",
  "Notes",
];
const FIXED_ORDER_AFTER_LINE = ["Remaining"];

/** Thick enough to cover the column resize knob; shifted left and slightly skinnier. */
const DIVIDER_SX = {
  borderLeft: (theme) => `5px solid ${theme.palette.divider}`,
  marginLeft: "-4px",
  pl: 2,
};

// ---------- Utilities ----------
const formatNumber = (value) => {
  if (typeof value !== "number") return value ?? "—";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseFloatSafe = (value, defaultValue = 0) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : defaultValue;
};

const toISODate = (d) => {
  if (!d || Number.isNaN(d.getTime?.())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/** Parse date-only ISO string (YYYY-MM-DD) as local date to avoid UTC-offset shifting the calendar day. */
const parseLocalDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}(T|$)/.test(s)) {
    const [datePart] = s.split("T");
    if (datePart) return new Date(datePart + "T12:00:00");
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

const parseMonthHeader = (hdr) => {
  const [m, y] = hdr.split(" ");
  if (!m || !y) return null;
  const d = new Date(`${m} 1, ${y}`);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), 1);
};

/** True if the month from header is before the current month. */
function isPastMonth(header) {
  const d = parseMonthHeader(header);
  if (!d) return false;
  const today = new Date();
  const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  return d < currentMonth;
}

// ---------- Normalizers ----------
/** Procore only accepts forecasts on rows that are both budgeted and budgetable. */
export function isLockedApiRow(raw) {
  if (!raw || (!("budgeted" in raw) && !("budgetable" in raw))) return false;
  return raw.budgeted !== true || raw.budgetable !== true;
}

function normalizeApiRow(raw, index) {
  const spreads = {};
  Object.entries(raw).forEach(([key, val]) => {
    if (key.startsWith("spread_")) {
      const parts = key.split("_"); // e.g., spread_august_2025
      if (parts.length >= 3) {
        const monthName = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
        const year = parts[2];
        const pretty = `${monthName} ${year}`;
        spreads[pretty] = parseFloatSafe(val?.amount);
      }
    }
  });

  const backendF2C =
    raw?.budget_forecast?.amount !== undefined && raw?.budget_forecast?.amount !== null
      ? parseFloatSafe(raw.budget_forecast.amount)
      : null;

  const result = {
    id: index,
    "Start Date": raw.start_date,
    "End Date": raw.end_date,
    Curve: raw.curve,
    Notes: raw.budget_notes ?? "",

    "Approved COs": parseFloatSafe(raw["Approved COs"]),
    "Projected Costs": parseFloatSafe(raw["Projected Costs"]),
    "Job to Date Costs": parseFloatSafe(raw["Job to Date Costs"]),
    "Forecast To Complete": backendF2C,
    Remaining: parseFloatSafe(raw.forecast_remainder),

    ...spreads,

    wbs_code: raw.wbs_code,
    description: raw.wbs_code?.description ?? "",
    locked: isLockedApiRow(raw),
  };

  // Ensure grouping keys exist on row (category, cost_code_level_1, cost_code_level_2, ...)
  if (raw.category !== undefined) result.category = raw.category;
  Object.entries(raw).forEach(([key]) => {
    if (/^cost_code_level_\d+$/.test(key)) result[key] = raw[key];
  });

  // Pass through any other displayable columns from backend
  Object.entries(raw).forEach(([key, val]) => {
    if (result[key] !== undefined || !shouldDisplayColumn(key)) return;
    if (key === "wbs_code" || key === "description") return;
    if (typeof val === "object" && val !== null && "amount" in val)
      result[key] = parseFloatSafe(val.amount);
    else if (typeof val === "number" && Number.isFinite(val)) result[key] = val;
    else if (typeof val === "string" && val.trim() !== "" && !Number.isNaN(parseFloat(val)))
      result[key] = parseFloatSafe(val);
    else result[key] = val;
  });

  return result;
}

function normalizeCsvRow(columns, rowValues, index) {
  const row = { id: index };
  columns.forEach((col, colIndex) => {
    const val = rowValues[colIndex];
    const canonical = canonicalMonthColumnFromCsvHeader(col);

    if (isMonthCol(canonical)) {
      row[canonical] = parseFloatSafe(val);
    } else if (canonical === "Start Date" || canonical === "End Date") {
      const d = new Date(val);
      row[canonical] = Number.isNaN(d.getTime()) ? "" : toISODate(d);
    } else if (
      typeof val === "number" ||
      (typeof val === "string" && val.trim() !== "" && !Number.isNaN(parseFloat(val)))
    ) {
      row[col] = parseFloatSafe(val);
    } else {
      row[col] = val;
    }
  });
  return row;
}

/**
 * Normalize df (API array or CSV shape) into the same row shape the table uses.
 * Used by Forecast page to get "original" rows for change detection.
 */
export function getNormalizedRowsFromDf(df) {
  if (!df) return [];
  if (Array.isArray(df)) return df.map((raw, idx) => normalizeApiRow(raw, idx));
  if (df.$columns && df.$data) {
    return df.$data.map((rowValues, idx) => normalizeCsvRow(df.$columns, rowValues, idx));
  }
  return [];
}

/** CSV may include Budget Code for row matching; only these + month cols are accepted. Others trigger error and discard. */
export function isAllowedCsvColumn(col) {
  if (!col || typeof col !== "string") return false;
  const s = col.trim();
  if (
    ["Budget Code", "Curve", "Start Date", "End Date", "Forecast To Complete", "Notes"].includes(s)
  )
    return true;
  if (isForecastCsvPeriodColumn(s)) return true;
  return false;
}

// ---------- Column Builders ----------
const TABLE_CONTROL_WIDTH = 150;
const editableCellStyle = {
  background: "rgba(25,118,210,0.08)",
  borderRadius: 6,
  // padding: "6px 8px",
  cursor: "text",
};
const editableInputStyle = {
  fontSize: "0.875rem",
  lineHeight: 1.43,
  minHeight: 22,
};

function createDescriptionColumn(grandTotals) {
  return {
    id: "Description",
    header: "Description",
    accessorFn: (row) => row.wbs_code?.flat_code || row["Budget Code"] || "—",
    size: 320,
    Footer: grandTotals
      ? () => (
          <Typography variant="body2" fontWeight="bold">
            Grand Totals
          </Typography>
        )
      : undefined,
    muiTableFooterCellProps: {
      align: "center",
      sx: { bgcolor: "grey.200" },
    },
    Cell: ({ row }) => {
      const code = row.original.wbs_code?.flat_code ?? row.original["Budget Code"] ?? "—";
      const desc = row.original.description ?? row.original["Budget Code Description"] ?? "";
      return (
        <Tooltip title={`${code} ${desc}`} arrow placement="top-start">
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {code}
            </Typography>
            {desc && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {desc}
              </Typography>
            )}
          </Box>
        </Tooltip>
      );
    },
    muiTableHeadCellProps: {
      align: "center",
      sx: {
        bgcolor: "background.paper",
        textAlign: "center",
      },
    },
    muiTableBodyCellProps: {
      align: "center",
      sx: {
        bgcolor: "background.paper",
        textAlign: "center",
      },
    },
  };
}

const CURVE_OPTIONS = [
  { value: "none", label: "Select Curve" },
  { value: "manual", label: "Manual" },
  { value: "linear", label: "Linear" },
  { value: "front_loaded", label: "Front-Loaded" },
  { value: "back_loaded", label: "Back-Loaded" },
  { value: "bell", label: "Bell" },
];

const LOCKED_ROW_BG = "#ffebee";

function createCurveColumn(handleEdit) {
  return {
    accessorKey: "Curve",
    header: "Curve",
    size: TABLE_CONTROL_WIDTH,
    muiTableHeadCellProps: {
      align: "center",
      sx: { minWidth: TABLE_CONTROL_WIDTH, maxWidth: TABLE_CONTROL_WIDTH },
    },
    muiTableBodyCellProps: {
      align: "center",
      sx: { minWidth: TABLE_CONTROL_WIDTH, maxWidth: TABLE_CONTROL_WIDTH },
    },
    Cell: ({ row }) => {
      const rowId = row.original.id;
      const raw = row.original.Curve?.toLowerCase() || "";
      const value = raw || "none";
      if (row.original.locked) {
        return (
          <Typography variant="body2" sx={{ textAlign: "center" }}>
            {CURVE_OPTIONS.find((c) => c.value === value && value !== "none")?.label ?? "—"}
          </Typography>
        );
      }
      return (
        <Box sx={{ ...editableCellStyle, background: "transparent" }}>
          <Select
            size="small"
            value={value}
            onChange={(e) =>
              handleEdit(rowId, "Curve", e.target.value === "none" ? "" : e.target.value)
            }
            sx={{
              width: TABLE_CONTROL_WIDTH,
              minWidth: TABLE_CONTROL_WIDTH,
              textAlign: "center",
              "& .MuiSelect-select": {
                textAlign: "center",
                fontSize: "0.95rem",
                py: 0.5,
              },
            }}
          >
            {CURVE_OPTIONS.map((curve) => (
              <MenuItem key={curve.value} value={curve.value}>
                {curve.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      );
    },
  };
}

function createDateColumn(field, handleEdit) {
  return {
    accessorKey: field,
    header: field,
    size: TABLE_CONTROL_WIDTH,
    muiTableHeadCellProps: {
      align: "center",
      sx: { minWidth: TABLE_CONTROL_WIDTH, maxWidth: TABLE_CONTROL_WIDTH },
    },
    muiTableBodyCellProps: {
      align: "center",
      sx: { minWidth: TABLE_CONTROL_WIDTH, maxWidth: TABLE_CONTROL_WIDTH },
    },
    Cell: ({ row }) => {
      const rowId = row.original.id;
      const value = row.original[field];
      const dateVal = parseLocalDate(value);

      if (row.original.locked) {
        return (
          <Typography variant="body2" sx={{ textAlign: "center" }}>
            {value || "—"}
          </Typography>
        );
      }

      return (
        <DatePicker
          slotProps={{
            textField: {
              size: "small",
              variant: "standard",
              sx: { width: TABLE_CONTROL_WIDTH, minWidth: TABLE_CONTROL_WIDTH },
            },
          }}
          value={dateVal}
          onChange={(newVal) => handleEdit(rowId, field, toISODate(newVal))}
        />
      );
    },
  };
}

function withDividerSx(col, hasDivider) {
  if (!hasDivider) return col;
  return {
    ...col,
    muiTableHeadCellProps: {
      ...col.muiTableHeadCellProps,
      align: col.muiTableHeadCellProps?.align ?? "center",
      sx: { ...col.muiTableHeadCellProps?.sx, ...DIVIDER_SX },
    },
    muiTableBodyCellProps: {
      ...col.muiTableBodyCellProps,
      align: col.muiTableBodyCellProps?.align ?? "center",
      sx: { ...col.muiTableBodyCellProps?.sx, ...DIVIDER_SX },
    },
  };
}

function createDataColumn(
  field,
  handleEdit,
  numericColumns,
  _rows,
  hasDividerBefore = false,
  grandTotals = null
) {
  const isMonth = isMonthCol(field);
  const isNumber = isMonth || numericColumns.has(field);
  const editable = isEditableColumn(field);
  const pastMonth = isMonth && isPastMonth(field);

  const col = {
    accessorKey: field,
    header: field,
    ...(isNumber && {
      aggregationFn: "sum",
      AggregatedCell: ({ cell }) => {
        const val = cell.getValue();
        const num = typeof val === "number" && Number.isFinite(val) ? val : null;
        return (
          <Typography variant="body2" fontWeight="bold" sx={{ textAlign: "center" }}>
            {num != null ? formatNumber(num) : "—"}
          </Typography>
        );
      },
      Footer:
        grandTotals && grandTotals[field] != null
          ? () => (
              <Typography variant="body2" fontWeight="bold" sx={{ textAlign: "center" }}>
                {formatNumber(grandTotals[field])}
              </Typography>
            )
          : undefined,
    }),
    muiTableHeadCellProps: {
      align: "center",
      ...(pastMonth && { sx: { color: "crimson", fontWeight: "bold" } }),
    },
    muiTableBodyCellProps: { align: "center" },
    ...(isNumber &&
      grandTotals && {
        muiTableFooterCellProps: { align: "center", sx: { minWidth: 80 } },
      }),
    Cell: ({ row }) => {
      const rowId = row.original.id;
      const value = row.original[field];
      const curve = String(row.original.Curve || "").toLowerCase();
      const hasCurve = curve && curve !== "none";

      // Monthly cells show N/A when no curve is set
      if (isMonth && !hasCurve) {
        return <span style={{ color: "#999", fontStyle: "italic" }}>N/A</span>;
      }

      if (!editable || row.original.locked) {
        return (
          <Typography variant="body2" sx={{ textAlign: "center" }}>
            {isNumber && (value === 0 || value) ? formatNumber(value) : (value ?? "—")}
          </Typography>
        );
      }

      const handleChange = (e) => {
        const newVal = e.target.value;
        if (isMonth) {
          if (curve !== "manual") {
            const ok = window.confirm(
              "This row is using a non-manual curve. Switch to Manual to edit monthly values?"
            );
            if (!ok) return; // user cancelled
            handleEdit(rowId, "Curve", "manual");
          }
        }
        handleEdit(rowId, field, isNumber ? (newVal === "" ? "" : parseFloatSafe(newVal)) : newVal);
      };

      return (
        <input
          type={isNumber ? "number" : "text"}
          value={value ?? ""}
          onChange={handleChange}
          style={{
            width: "100%",
            border: "none",
            background: "transparent",
            textAlign: "center",
            ...editableCellStyle,
            ...editableInputStyle,
          }}
        />
      );
    },
  };
  return withDividerSx(col, hasDividerBefore);
}

// ---------- Component ----------
export default function ForecastTable({
  df,
  views = [],
  selectedViewId = null,
  onViewChange = () => {},
  snapshots = [],
  selectedSnapshot = "Current",
  onSnapshotChange = () => {},
  onOpenSubmit = () => {},
  onRowsChange = () => {},
  showCharts = true,
  onToggleCharts = () => {},
}) {
  // Normalize data (no client-side calculated columns; backend sends what to show)
  const initialRows = useMemo(() => {
    if (!df) return [];

    if (Array.isArray(df)) {
      return df.map((raw, idx) => normalizeApiRow(raw, idx));
    }

    if (df.$columns && df.$data) {
      return df.$data.map((rowValues, idx) => normalizeCsvRow(df.$columns, rowValues, idx));
    }

    return [];
  }, [df]);

  const [rows, setRows] = useState(initialRows);
  useEffect(() => setRows(initialRows), [initialRows]);
  const handleEdit = useCallback((rowId, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId && !row.locked ? { ...row, [field]: value } : row))
    );
  }, []);

  // Push edits up to parent so it can save JSON if needed
  useEffect(() => {
    onRowsChange(rows);
  }, [rows, onRowsChange]);

  // Local grouping & hide-past-months state (internal to table)
  const [grouping, setGrouping] = useState([]);
  const [unpinnedGroups, setUnpinnedGroups] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [hidePastMonths, setHidePastMonths] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({});

  // Collect columns from data; filter to displayable (no _, must start with uppercase; month cols allowed)
  const allColumns = useMemo(() => {
    const keys = new Set();
    rows.forEach((r) => Object.keys(r).forEach((k) => keys.add(k)));
    return Array.from(keys);
  }, [rows]);

  const [orderedColumns, firstColumnAfterLineIndex] = useMemo(() => {
    const displayable = allColumns.filter((k) => shouldDisplayColumn(k));
    const nonMonth = displayable.filter((k) => !isMonthCol(k));
    let monthColumns = displayable.filter(isMonthCol);

    if (hidePastMonths) {
      const today = new Date();
      const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      monthColumns = monthColumns.filter((hdr) => {
        const d = parseMonthHeader(hdr);
        return d && d >= currentMonth;
      });
    }

    monthColumns.sort((a, b) => {
      const dA = parseMonthHeader(a);
      const dB = parseMonthHeader(b);
      if (!dA || !dB) return 0;
      return dA.getTime() - dB.getTime();
    });

    // Other columns (not fixed-order, not after-divider): sort alphabetically
    const fixedSet = new Set([...FIXED_ORDER_BEFORE_LINE, ...FIXED_ORDER_AFTER_LINE]);
    const otherCols = nonMonth.filter((c) => !fixedSet.has(c));
    otherCols.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

    // Before divider: alphabetical others, then Start Date, End Date, Curve, Forecast To Complete, Notes
    const fixedBeforeLine = FIXED_ORDER_BEFORE_LINE.filter((c) => nonMonth.includes(c));
    const beforeLine = [...otherCols, ...fixedBeforeLine];

    // After divider: Remaining then month columns
    const fixedAfterLine = FIXED_ORDER_AFTER_LINE.filter((c) => nonMonth.includes(c));
    const afterLine = [...fixedAfterLine, ...monthColumns];

    const list = [...beforeLine, ...afterLine];
    const firstAfterLineIndex = beforeLine.length;
    return [list, firstAfterLineIndex];
  }, [allColumns, hidePastMonths]);

  // Detect numeric columns for input type and formatting
  const numericColumns = useMemo(() => {
    const set = new Set();
    orderedColumns.forEach((field) => {
      if (isMonthCol(field)) {
        set.add(field);
        return;
      }
      const row = rows.find((r) => r[field] !== undefined && r[field] !== null && r[field] !== "");
      if (!row) return;
      const v = row[field];
      if (typeof v === "number" && Number.isFinite(v)) set.add(field);
      else if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(parseFloat(v)))
        set.add(field);
    });
    return set;
  }, [rows, orderedColumns]);

  const handleExportCsv = useCallback(() => {
    if (!rows.length) return;
    const csvCell = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const exportCols = orderedColumns.filter(
      (c) => c !== "id" && c !== "Budget Code" && c !== "Budget Code Description"
    );
    const header = ["Budget Code", "Budget Code Description", ...exportCols]
      .map(csvCell)
      .join(",");
    // Rows without a budget code (e.g. Procore's "None" row) can't be matched on re-upload.
    const exportRows = rows.filter((row) => row.wbs_code?.flat_code ?? row["Budget Code"]);
    const dataRows = exportRows.map((row) =>
      [
        row.wbs_code?.flat_code ?? row["Budget Code"],
        row.wbs_code?.description ?? row.description ?? row["Budget Code Description"],
        ...exportCols.map((col) => row[col]),
      ]
        .map(csvCell)
        .join(",")
    );
    const csv = [header, ...dataRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `forecast-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [rows, orderedColumns]);

  const availableGroupingKeys = useMemo(() => {
    const hasValue = (val) =>
      val != null && String(val).trim() !== "" && String(val) !== "None";
    const keys = allColumns.filter(
      (key) => isGroupingKey(key) && rows.some((r) => hasValue(r[key]))
    );
    return keys.sort((a, b) => {
      if (a === "category") return -1;
      if (b === "category") return 1;
      const numA = parseInt(a.replace(/^cost_code_level_(\d+)$/, "$1"), 10);
      const numB = parseInt(b.replace(/^cost_code_level_(\d+)$/, "$1"), 10);
      return Number.isNaN(numA) || Number.isNaN(numB) ? 0 : numA - numB;
    });
  }, [allColumns, rows]);

  const grandTotals = useMemo(() => {
    const totals = {};
    orderedColumns.forEach((field) => {
      if (!isMonthCol(field) && !numericColumns.has(field)) return;
      let sum = 0;
      rows.forEach((row) => {
        const v = row[field];
        if (typeof v === "number" && Number.isFinite(v)) sum += v;
        else if (v != null && v !== "" && !Number.isNaN(parseFloat(v))) sum += parseFloat(v);
      });
      totals[field] = sum;
    });
    return totals;
  }, [rows, orderedColumns, numericColumns]);

  const columns = useMemo(() => {
    const descriptionColumn = createDescriptionColumn(grandTotals);

    const hiddenGroupingCols = availableGroupingKeys.map((accessorKey) => ({
      accessorKey,
      header: getGroupingColumnLabel(accessorKey),
      minSize: 200,
      size: 200,
      Header: ({ column, table }) => {
        const accessorKey = column.columnDef.accessorKey;
        const isPinned = !unpinnedGroups.includes(accessorKey);
        const groupingState = table.getState().grouping || [];
        const depth = groupingState.indexOf(accessorKey);
        const rowsAtDepth =
          depth >= 0
            ? table.getRowModel().rows.filter((r) => r.getIsGrouped?.() && r.depth === depth)
            : [];
        const allExpanded = rowsAtDepth.length > 0 && rowsAtDepth.every((r) => r.getIsExpanded?.());
        const handleExpandCollapseAll = (e) => {
          e.stopPropagation();
          if (rowsAtDepth.length === 0) return;
          table.setExpanded((prev) => {
            const expand = !allExpanded;
            const next = prev === true ? {} : { ...(prev || {}) };
            if (prev === true) {
              table.getRowModel().rows.forEach((r) => {
                next[r.id] = rowsAtDepth.includes(r) ? expand : r.getIsExpanded?.();
              });
            } else {
              rowsAtDepth.forEach((r) => {
                next[r.id] = expand;
              });
            }
            return next;
          });
        };
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {depth >= 0 && rowsAtDepth.length > 0 && (
              <Tooltip title={allExpanded ? "Collapse all" : "Expand all"}>
                <IconButton size="small" onClick={handleExpandCollapseAll} sx={{ p: 0.5 }}>
                  {allExpanded ? (
                    <KeyboardArrowDownIcon fontSize="small" />
                  ) : (
                    <KeyboardArrowRightIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {getGroupingColumnLabel(accessorKey)}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setUnpinnedGroups((prev) =>
                  isPinned ? [...prev, accessorKey] : prev.filter((k) => k !== accessorKey)
                );
              }}
              sx={{ p: 0.5 }}
            >
              <PushPinIcon
                fontSize="small"
                sx={{
                  transform: isPinned ? "none" : "rotate(45deg)",
                  color: isPinned ? "primary.main" : "action.disabled",
                }}
              />
            </IconButton>
          </Box>
        );
      },
      GroupedCell: ({ cell, row }) => {
        const value = cell.getValue();
        const label = value != null && value !== "" ? String(value) : "";
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
            }}
            onClick={row.getToggleExpandedHandler()}
          >
            <IconButton
              size="small"
              sx={{ p: 0.5, ml: -1 }}
              onClick={(e) => {
                e.stopPropagation();
                row.toggleExpanded();
              }}
            >
              {row.getIsExpanded() ? (
                <KeyboardArrowDownIcon fontSize="small" />
              ) : (
                <KeyboardArrowRightIcon fontSize="small" />
              )}
            </IconButton>
            <Typography
              variant="body2"
              fontWeight="bold"
              noWrap
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
                display: "block",
              }}
              title={label}
            >
              Subtotal {label}
            </Typography>
          </Box>
        );
      },
      muiTableHeadCellProps: {
        sx: { minWidth: 200 },
      },
      muiTableBodyCellProps: {
        sx: { overflow: "hidden", minWidth: 200 },
      },
    }));

    const otherColumns = orderedColumns
      .filter((field) => allColumns.includes(field) || isMonthCol(field))
      .map((field, index) => {
        const hasDividerBefore = index === firstColumnAfterLineIndex;
        if (field === "Curve")
          return withDividerSx(createCurveColumn(handleEdit), hasDividerBefore);
        if (field === "Start Date" || field === "End Date")
          return withDividerSx(createDateColumn(field, handleEdit), hasDividerBefore);
        return createDataColumn(
          field,
          handleEdit,
          numericColumns,
          rows,
          hasDividerBefore,
          grandTotals
        );
      });

    return [descriptionColumn, ...hiddenGroupingCols, ...otherColumns];
  }, [
    handleEdit,
    orderedColumns,
    allColumns,
    numericColumns,
    rows,
    firstColumnAfterLineIndex,
    availableGroupingKeys,
    grandTotals,
    unpinnedGroups,
  ]);

  useEffect(() => {
    // show grouped columns; hide non-grouped grouping columns
    const vis = {};
    availableGroupingKeys.forEach((k) => {
      vis[k] = grouping.includes(k);
    });
    setColumnVisibility(vis);
  }, [grouping, availableGroupingKeys]);

  // Expand column right before Description: [grouping cols..., Description, ...rest]
  const columnOrder = useMemo(() => {
    const rest = [
      ...availableGroupingKeys.filter((k) => !grouping.includes(k)),
      ...orderedColumns.filter((f) => f !== "Description" && !availableGroupingKeys.includes(f)),
    ];
    return [...grouping, "Description", ...rest];
  }, [grouping, orderedColumns, availableGroupingKeys]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: "100%" }}>
        <MaterialReactTable
          columns={columns}
          data={rows}
          getGroupedRowModel={getGroupedRowModel()}
          enableStickyHeader
          enableStickyFooter
          enableRowVirtualization
          enableColumnResizing
          enableSorting={false}
          enableHiding={false}
          layoutMode="grid-auto"
          enablePagination={false}
          enableBottomToolbar={false}
          enableColumnOrdering={false}
          enableColumnDragging={false}
          enableColumnPinning
          // Grouping controlled only via dropdown (no column menu / no dropzone)
          enableGrouping
          enableExpanding
          enableColumnActions={false}
          positionToolbarDropZone="none"
          // Filters off
          enableColumnFilters={false}
          enableFacetedValues={false}
          enableGlobalFilter={false}
          // Avoid auto resets
          autoResetAll={false}
          autoResetExpanded={false}
          autoResetGrouping={false}
          muiTableBodyRowProps={({ row }) => {
            if (row.getIsGrouped()) {
              return { sx: { bgcolor: "action.hover", "& td": { fontWeight: "bold" } } };
            }
            if (row.original?.locked) {
              return { sx: { "& td": { bgcolor: LOCKED_ROW_BG } } };
            }
            return {};
          }}
          muiTableFooterRowProps={{
            sx: { bgcolor: "grey.200", "& td": { fontWeight: "bold" } },
          }}
          displayColumnDefOptions={{
            "mrt-row-expand": {
              size: 0,
              minSize: 0,
              maxSize: 0,
              muiTableHeadCellProps: { sx: { display: "none" } },
              muiTableBodyCellProps: { sx: { display: "none" } },
            },
          }}
          initialState={{
            density: "compact",
            expanded: true,
            columnPinning: { left: ["Description"] },
          }}
          state={{
            grouping,
            columnVisibility,
            columnOrder,
            expanded,
            columnPinning: {
              left: [...grouping.filter((g) => !unpinnedGroups.includes(g)), "Description"],
            },
          }}
          onExpandedChange={(updater) => {
            setExpanded((prev) => (typeof updater === "function" ? updater(prev) : updater));
          }}
          onGroupingChange={(updater) => {
            setGrouping((prev) => (typeof updater === "function" ? updater(prev) : prev));
          }}
          renderTopToolbarCustomActions={() => (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", px: 1, width: "100%" }}>
              {/* View Select */}
              <FormControl
                size="small"
                sx={{ minWidth: 200 }}
                title="Select budget view (forecasting)"
              >
                <InputLabel id="view-select-label">Select View</InputLabel>
                <Select
                  labelId="view-select-label"
                  label="Select View"
                  value={selectedViewId ?? ""}
                  onChange={(e) => onViewChange(Number(e.target.value))}
                  displayEmpty
                >
                  {views.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Snapshot Select */}
              <FormControl
                size="small"
                sx={{ minWidth: 220 }}
                title="Select a saved snapshot or Current"
              >
                <InputLabel id="snapshot-select-label">Select Snapshot</InputLabel>
                <Select
                  labelId="snapshot-select-label"
                  label="Select Snapshot"
                  value={selectedSnapshot || "Current"}
                  renderValue={(v) => v}
                  onChange={(e) => onSnapshotChange(e.target.value)}
                >
                  {snapshots.map((snap) => (
                    <MenuItem
                      key={snap.snapshot_id ?? snap.snapshot_name}
                      value={snap.snapshot_name}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <span>{snap.snapshot_name}</span>
                        {snap.created_at && (
                          <Typography variant="caption" color="text.secondary">
                            {formatSnapshotTime(snap.created_at)}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Multi-select Grouping (internal) */}
              <FormControl
                size="small"
                sx={{ minWidth: 260 }}
                title="Group rows by cost code, category, or other fields"
              >
                <InputLabel id="grouping-select-label">Grouping</InputLabel>
                <Select
                  multiple
                  labelId="grouping-select-label"
                  label="Grouping"
                  value={grouping}
                  onChange={(e) => setGrouping(e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip key={val} label={getGroupingColumnLabel(val)} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {availableGroupingKeys.map((k) => (
                    <MenuItem key={k} value={k}>
                      {getGroupingColumnLabel(k)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Toggles */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  ml: 1,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">Hide past months</Typography>
                  <Switch checked={hidePastMonths} onChange={() => setHidePastMonths((p) => !p)} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">Plot Charts</Typography>
                  <Switch
                    size="small"
                    checked={showCharts}
                    onChange={(e) => onToggleCharts(e.target.checked)}
                    inputProps={{ "aria-label": "Toggle charts based on current table values" }}
                  />
                </Box>
              </Box>

              {/* Spacer then Export + Save buttons */}
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Download current table as CSV for editing in Excel">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportCsv}
                  disabled={!rows.length}
                >
                  Export CSV
                </Button>
              </Tooltip>
              <Tooltip title="Save edits or uploaded CSV to Procore">
                <Button variant="contained" size="small" onClick={onOpenSubmit}>
                  Save Table
                </Button>
              </Tooltip>
            </Box>
          )}
          muiTableContainerProps={{
            sx: {
              maxHeight: "75vh",
            },
          }}
        />
      </Box>
    </LocalizationProvider>
  );
}

ForecastTable.propTypes = {
  df: PropTypes.object,
  views: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  selectedViewId: PropTypes.number,
  onViewChange: PropTypes.func,
  snapshots: PropTypes.arrayOf(PropTypes.object),
  selectedSnapshot: PropTypes.string,
  onSnapshotChange: PropTypes.func,
  onOpenSubmit: PropTypes.func,
  onRowsChange: PropTypes.func,
  showCharts: PropTypes.bool,
  onToggleCharts: PropTypes.func,
};
